import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Brain, RotateCcw, ArrowLeft, Volume2, VolumeX, Play, Trophy } from "lucide-react";
import { CocoonMark } from "@/components/CocoonMark";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";

const L = {
  fr: {
    title: "L'Écho des Lueurs", tagline: "Un jeu de mémoire lumineux.",
    intro: "Le Cocon murmure une suite de lueurs. Retiens-la, répète-la — et vois jusqu'où ta mémoire peut danser.",
    start: "Commencer", round: "Manche", best: "Record", watch: "Observe la suite…", repeat: "À toi de répéter",
    goodTitle: "Bien joué !", overTitle: "Ta mémoire a flanché", reached: (n) => `Suite de ${n} lueurs`,
    newBest: "Nouveau record !", replay: "Rejouer", home: "Accueil",
    soundOn: "Couper le son", soundOff: "Activer le son",
    f1: "6 lueurs", f2: "Séquences infinies", f3: "Mémoire pure",
  },
  en: {
    title: "The Echo of Glimmers", tagline: "A luminous memory game.",
    intro: "The Cocoon whispers a string of glimmers. Hold it, echo it — and see how far your memory can dance.",
    start: "Begin", round: "Round", best: "Best", watch: "Watch the sequence…", repeat: "Your turn to echo",
    goodTitle: "Well played!", overTitle: "Your memory faltered", reached: (n) => `A run of ${n} glimmers`,
    newBest: "New record!", replay: "Play again", home: "Home",
    soundOn: "Mute", soundOff: "Unmute",
    f1: "6 glimmers", f2: "Endless sequences", f3: "Pure memory",
  },
  es: {
    title: "El Eco de los Destellos", tagline: "Un juego de memoria luminoso.",
    intro: "El Cocoon susurra una serie de destellos. Retenla, repítela — y descubre hasta dónde baila tu memoria.",
    start: "Empezar", round: "Ronda", best: "Récord", watch: "Observa la secuencia…", repeat: "Te toca repetir",
    goodTitle: "¡Bien hecho!", overTitle: "Tu memoria falló", reached: (n) => `Una serie de ${n} destellos`,
    newBest: "¡Nuevo récord!", replay: "Jugar de nuevo", home: "Inicio",
    soundOn: "Silenciar", soundOff: "Activar sonido",
    f1: "6 destellos", f2: "Secuencias infinitas", f3: "Memoria pura",
  },
  nl: {
    title: "De Echo van Glinsteringen", tagline: "Een lichtgevend geheugenspel.",
    intro: "De Cocon fluistert een reeks glinsteringen. Onthoud ze, herhaal ze — en zie hoe ver je geheugen kan dansen.",
    start: "Beginnen", round: "Ronde", best: "Record", watch: "Bekijk de reeks…", repeat: "Jouw beurt om te herhalen",
    goodTitle: "Goed gespeeld!", overTitle: "Je geheugen haperde", reached: (n) => `Een reeks van ${n} glinsteringen`,
    newBest: "Nieuw record!", replay: "Opnieuw spelen", home: "Home",
    soundOn: "Dempen", soundOff: "Geluid aan",
    f1: "6 glinsteringen", f2: "Eindeloze reeksen", f3: "Puur geheugen",
  },
};

// 6 gemmes lumineuses, cohérentes avec la charte (fond sombre, accent doré).
const PADS = [
  { hue: 38, tone: 329.63 },   // ambre / or
  { hue: 345, tone: 392.0 },   // rose chaud
  { hue: 175, tone: 440.0 },   // turquoise
  { hue: 265, tone: 523.25 },  // violet
  { hue: 14, tone: 587.33 },   // corail
  { hue: 140, tone: 659.25 },  // vert doux
];

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Memoire() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = L[lang] || L.fr;

  const [phase, setPhase] = useState("intro"); // intro | showing | input | over
  const [sequence, setSequence] = useState([]);
  const [inputIdx, setInputIdx] = useState(0);
  const [active, setActive] = useState(null);
  const [best, setBest] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const [newBest, setNewBest] = useState(false);

  const audioRef = useRef(null);
  const soundRef = useRef(true);
  const seqRef = useRef([]);

  useEffect(() => { soundRef.current = soundOn; }, [soundOn]);
  useEffect(() => {
    try { setBest(Number(localStorage.getItem("coccon_memoire_best") || 0)); } catch { /* noop */ }
  }, []);
  useEffect(() => () => { try { audioRef.current?.close(); } catch { /* noop */ } }, []);

  const round = sequence.length;

  const tone = useCallback((freq, dur = 0.32, kind = "sine") => {
    if (!soundRef.current) return;
    try {
      if (!audioRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioRef.current = new AC();
      }
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t = ctx.currentTime;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = kind;
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.28, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t + dur + 0.02);
    } catch { /* noop */ }
  }, []);

  const flash = async (i, dur) => {
    setActive(i);
    tone(PADS[i].tone, Math.max(0.18, dur / 1000 * 0.7));
    await wait(dur);
    setActive(null);
    await wait(140);
  };

  const playSequence = useCallback(async (seq) => {
    setPhase("showing");
    setInputIdx(0);
    await wait(650);
    const dur = Math.max(280, 620 - (seq.length - 1) * 22);
    for (const i of seq) {
      await flash(i, dur);
    }
    setPhase("input");
  }, []);

  const start = async () => {
    setNewBest(false);
    const seq = [Math.floor(Math.random() * PADS.length)];
    seqRef.current = seq;
    setSequence(seq);
    // amorce l'audio via le geste utilisateur
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!audioRef.current) audioRef.current = new AC();
      if (audioRef.current.state === "suspended") audioRef.current.resume();
    } catch { /* noop */ }
    await playSequence(seq);
  };

  const gameOver = () => {
    tone(120, 0.5, "sawtooth");
    const reached = Math.max(0, seqRef.current.length - 1);
    setPhase("over");
    if (reached > best) {
      setBest(reached);
      setNewBest(true);
      try { localStorage.setItem("coccon_memoire_best", String(reached)); } catch { /* noop */ }
    }
  };

  const press = async (i) => {
    if (phase !== "input") return;
    setActive(i);
    tone(PADS[i].tone, 0.3);
    await wait(180);
    setActive(null);
    const expected = seqRef.current[inputIdx];
    if (i !== expected) { gameOver(); return; }
    const nextIdx = inputIdx + 1;
    if (nextIdx === seqRef.current.length) {
      // manche réussie
      setPhase("showing");
      await wait(560);
      const seq = [...seqRef.current, Math.floor(Math.random() * PADS.length)];
      seqRef.current = seq;
      setSequence(seq);
      await playSequence(seq);
    } else {
      setInputIdx(nextIdx);
    }
  };

  const RADIUS = 34; // % du conteneur
  return (
    <div className="relative min-h-[100svh] bg-background text-foreground overflow-x-clip flex flex-col" data-testid="memoire-page">
      <div className="grain-overlay" aria-hidden />
      <div aria-hidden className="fixed inset-0 pointer-events-none z-0"
        style={{ background: "radial-gradient(120% 90% at 50% 6%, hsl(38 90% 50% / 0.16), transparent 55%), radial-gradient(100% 80% at 50% 110%, hsl(265 70% 45% / 0.12), transparent 60%)" }} />

      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3">
            {phase !== "intro" && (
              <button onClick={() => setSoundOn((v) => !v)} data-testid="memoire-sound"
                aria-label={soundOn ? c.soundOn : c.soundOff}
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors">
                {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
            )}
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/" className="text-sm text-foreground/60 hover:text-primary transition-colors inline-flex items-center gap-1.5"><ArrowLeft size={15} /> {c.home}</Link>
          </div>
        </div>
      </header>

      {phase === "intro" ? (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pt-24 pb-16">
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7 }}
            className="mb-6 w-16 h-16 rounded-2xl bg-primary/15 border border-primary/40 flex items-center justify-center text-primary">
            <Brain size={30} />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20, filter: "blur(8px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 0.9 }}
            className="font-display text-gradient-orange text-5xl sm:text-6xl lg:text-7xl leading-none pb-[0.05em]" data-testid="memoire-title">{c.title}</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="mt-3 text-primary/70 uppercase tracking-[0.2em] text-xs">{c.tagline}</motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-6 max-w-xl text-foreground/70 text-lg font-display italic leading-relaxed">{c.intro}</motion.p>

          <div className="mt-6 flex items-center gap-2 text-sm text-foreground/60">
            <Trophy size={15} className="text-primary" /> {c.best} · <span className="font-display text-primary text-lg">{best}</span>
          </div>

          <motion.button initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
            onClick={start} data-testid="memoire-start"
            className="btn-frost-orange mt-8 px-10 py-4 rounded-full font-semibold text-base inline-flex items-center gap-2">
            <Play size={18} /> {c.start}
          </motion.button>
        </main>
      ) : (
        <main className="relative z-10 flex-1 flex flex-col items-center px-4 sm:px-6 pt-20 pb-14 w-full">
          {/* HUD */}
          <div className="w-full max-w-md flex items-center justify-between gap-4 glass rounded-2xl border border-white/10 px-5 py-3 mb-6" data-testid="memoire-hud">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.14em] text-foreground/45">{c.round}</span>
              <span className="font-display text-2xl text-gradient-orange leading-none" data-testid="memoire-round">{round}</span>
            </div>
            <div className="flex items-center gap-1.5 text-foreground/70">
              <Trophy size={14} className="text-primary/70" />
              <span className="font-display text-xl leading-none">{best}</span>
            </div>
          </div>

          {/* Indicateur d'état */}
          <div className="h-6 mb-4">
            <AnimatePresence mode="wait">
              {phase === "showing" && <motion.p key="w" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm uppercase tracking-[0.2em] text-primary/70">{c.watch}</motion.p>}
              {phase === "input" && <motion.p key="r" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm uppercase tracking-[0.2em] text-foreground/60">{c.repeat}</motion.p>}
            </AnimatePresence>
          </div>

          {/* Anneau de 6 lueurs */}
          {phase !== "over" && (
            <div className="relative mx-auto" style={{ width: 360, height: 360, maxWidth: "88vw", maxHeight: "88vw" }} data-testid="memoire-board">
              <motion.div aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/15"
                style={{ width: "78%", height: "78%" }} animate={{ rotate: 360 }} transition={{ duration: 60, repeat: Infinity, ease: "linear" }} />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full glass border border-white/10 flex items-center justify-center text-foreground/40">
                <Brain size={22} />
              </div>
              {PADS.map((p, i) => {
                const a = (i / PADS.length) * 2 * Math.PI - Math.PI / 2;
                const x = 50 + Math.cos(a) * RADIUS;
                const y = 50 + Math.sin(a) * RADIUS;
                const on = active === i;
                return (
                  <motion.button key={i} onClick={() => press(i)} data-testid={`memoire-pad-${i}`}
                    disabled={phase !== "input"}
                    className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full focus:outline-none"
                    style={{ left: `${x}%`, top: `${y}%`, width: "27%", height: "27%" }}
                    animate={{ scale: on ? 1.12 : 1 }} transition={{ duration: 0.12 }}
                    whileTap={phase === "input" ? { scale: 0.94 } : undefined}
                  >
                    <span className="block w-full h-full rounded-full border transition-all"
                      style={{
                        background: on
                          ? `radial-gradient(circle at 35% 30%, hsl(${p.hue} 95% 72%), hsl(${p.hue} 90% 52%))`
                          : `radial-gradient(circle at 35% 30%, hsl(${p.hue} 70% 40% / 0.55), hsl(${p.hue} 65% 26% / 0.5))`,
                        borderColor: on ? `hsl(${p.hue} 95% 75%)` : `hsl(${p.hue} 70% 45% / 0.5)`,
                        boxShadow: on ? `0 0 44px 4px hsl(${p.hue} 95% 60% / 0.85), inset 0 0 20px hsl(${p.hue} 95% 80% / 0.6)` : `0 0 14px hsl(${p.hue} 80% 45% / 0.25)`,
                      }} />
                  </motion.button>
                );
              })}
            </div>
          )}

          {/* Fin de partie */}
          {phase === "over" && (
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ type: "spring", damping: 18 }}
              className="mt-8 w-full max-w-md text-center glass rounded-[28px] border border-primary/25 p-8 sm:p-10" data-testid="memoire-over">
              <Brain className="mx-auto text-foreground/50 mb-5" size={44} />
              <h2 className="font-display text-3xl sm:text-4xl text-foreground/90">{c.overTitle}</h2>
              <p className="text-primary text-xl mt-3 font-semibold" data-testid="memoire-score">{c.reached(Math.max(0, sequence.length - 1))}</p>
              {newBest && <p className="mt-2 text-sm uppercase tracking-[0.2em] text-primary/80 inline-flex items-center gap-1.5"><Trophy size={14} /> {c.newBest}</p>}
              <div className="mt-4 text-sm text-foreground/55 flex items-center justify-center gap-2">
                <Trophy size={14} className="text-primary/70" /> {c.best} · <span className="font-display text-primary text-lg">{best}</span>
              </div>
              <button onClick={start} data-testid="memoire-replay" className="btn-frost-orange mt-7 w-full py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2">
                <RotateCcw size={16} /> {c.replay}
              </button>
              <Link to="/" className="block mt-3 text-sm text-foreground/50 hover:text-primary transition-colors">{c.home}</Link>
            </motion.div>
          )}
        </main>
      )}

      {phase === "intro" && <Footer />}
    </div>
  );
}
